import { Box } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import ChartSection from '../shared/ChartSection';
import ChartTooltip from '../shared/ChartTooltip';
import useNivoTheme from '../shared/useNivoTheme';

export default function SectorVsCPI({ data }) {
    const { theme, colors } = useNivoTheme();
    if (!data || data.length === 0) return null;

    const pctMax = Math.max(
        ...data.map(d => Math.max(d.tech_pct || 0, d.energy_pct || 0))
    );
    const cpiVals = data.map(d => d.cpi_change);
    const cpiMin = Math.min(...cpiVals, 0);
    const cpiMax = Math.max(...cpiVals, 0);
    const cpiSpan = cpiMax - cpiMin || 1;

    const rescaleCpi = v => ((v - cpiMin) / cpiSpan) * pctMax;
    const unscaleCpi = v => (v / pctMax) * cpiSpan + cpiMin;

    const lineData = [
        { id: 'Tech %', data: data.map(d => ({ x: d.month, y: d.tech_pct })) },
        { id: 'Energy %', data: data.map(d => ({ x: d.month, y: d.energy_pct })) },
        {
            id: 'CPI Change %',
            data: data.map(d => ({ x: d.month, y: rescaleCpi(d.cpi_change) })),
        },
    ];

    const tickValues = data.filter((_, i) => i % 12 === 0).map(d => d.month);

    const RightAxisLayer = ({ innerHeight, innerWidth }) => {
        const ticks = 5;
        const vals = Array.from({ length: ticks + 1 }, (_, i) =>
            cpiMin + (i * cpiSpan) / ticks
        );
        return (
            <g transform={`translate(${innerWidth}, 0)`}>
                <line
                    x1={0}
                    y1={0}
                    x2={0}
                    y2={innerHeight}
                    stroke={theme.axis.domain.line.stroke}
                />
                {vals.map((v, i) => {
                    const y = innerHeight - (i * innerHeight) / ticks;
                    return (
                        <g key={i} transform={`translate(0, ${y})`}>
                            <line
                                x1={0}
                                y1={0}
                                x2={5}
                                y2={0}
                                stroke={theme.axis.ticks.line.stroke}
                            />
                            <text
                                x={9}
                                y={0}
                                dy="0.32em"
                                fill={theme.axis.ticks.text.fill}
                                fontSize={theme.axis.ticks.text.fontSize}
                                fontFamily={theme.axis.ticks.text.fontFamily}
                            >
                                {v.toFixed(1) + '%'}
                            </text>
                        </g>
                    );
                })}
                <text
                    transform={`translate(50, ${innerHeight / 2}) rotate(90)`}
                    textAnchor="middle"
                    fill={theme.axis.legend.text.fill}
                    fontSize={theme.axis.legend.text.fontSize}
                >
                    CPI Change %
                </text>
            </g>
        );
    };

    return (
        <ChartSection
            id="sector-cpi"
            title="Do they rotate into inflation-sensitive sectors before CPI spikes?"
            subtitle="Tech and Energy as % of congressional trades, overlaid with CPI monthly change (right axis)"
        >
            <Box sx={{ height: 400 }}>
                <ResponsiveLine
                    data={lineData}
                    margin={{ top: 30, right: 90, bottom: 60, left: 55 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: pctMax }}
                    curve="monotoneX"
                    enablePoints={false}
                    enableGridX={false}
                    colors={[colors.primary, '#f59e0b', colors.error]}
                    lineWidth={2}
                    axisBottom={{ tickRotation: 45, tickValues }}
                    axisLeft={{
                        format: v => v + '%',
                        legend: 'Sector %',
                        legendPosition: 'middle',
                        legendOffset: -45,
                    }}
                    legends={[
                        {
                            anchor: 'top',
                            direction: 'row',
                            translateY: -20,
                            itemWidth: 110,
                            itemHeight: 16,
                            symbolSize: 8,
                            itemTextColor: colors.secondary,
                        },
                    ]}
                    layers={[
                        'grid',
                        'markers',
                        'axes',
                        'areas',
                        'crosshair',
                        'lines',
                        'points',
                        'slices',
                        'mesh',
                        'legends',
                        RightAxisLayer,
                    ]}
                    enableSlices="x"
                    sliceTooltip={({ slice }) => (
                        <ChartTooltip
                            title={String(slice.points[0].data.x)}
                            rows={slice.points.map(p => {
                                const isCpi = p.seriesId === 'CPI Change %';
                                const realY = isCpi ? unscaleCpi(p.data.y) : p.data.y;
                                return {
                                    label: p.seriesId,
                                    value: realY.toFixed(2) + '%',
                                    color: p.seriesColor,
                                };
                            })}
                        />
                    )}
                    theme={theme}
                />
            </Box>
        </ChartSection>
    );
}
